import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { isEqual } from 'lodash'
import { currency } from 'src/data/currency'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import {
	fetchProjectsByTenantIdUtil,
	fetchPurchaseOrdersByTenantIdUtil,
	fetchSuppliersByTenantIdUtil,
} from 'src/utils/fetchUtils'
import {
	makeErrorToast,
	makeSuccessToast,
	makeWarningToast,
	msg,
} from 'src/utils/makeToast'
import { updateCompanyInfoWithDeltaSyncSuppress } from 'src/utils/miscellaneous'
import {
	createPurchases,
	fetchCurrentPurchase,
	setCurrentPurchase,
} from './purchases'

export const fetchPurchaseOrders = createAsyncThunk(
	'purchaseOrders/fetchPurchaseOrders',
	async (arg, { getState, rejectWithValue }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		try {
			const purchaseOrdersDataPromise = fetchPurchaseOrdersByTenantIdUtil(
				userTableInfo.currentCompanyTenantId
			)
			const suppliersDataPromise = fetchSuppliersByTenantIdUtil(
				userTableInfo.currentCompanyTenantId
			)
			const projectsDataPromise = fetchProjectsByTenantIdUtil(
				userTableInfo.currentCompanyTenantId
			)
			const [purchaseOrdersData, suppliersData, projectsData] =
				await Promise.all([
					purchaseOrdersDataPromise,
					suppliersDataPromise,
					projectsDataPromise,
				])

			purchaseOrdersData.map((i) => {
				const currentSupplierData = suppliersData.filter(
					(sd) => sd.id === i.supplierId
				)

				const currentSupplierName =
					currentSupplierData.length > 0
						? currentSupplierData[0].name
						: i.supplierName || 'Deleted'
				i.supplierName = currentSupplierName
				const currentSupplierCurrency =
					currentSupplierData.length > 0
						? currentSupplierData[0].currency
						: 'EUR'
				const findCurrency = currency.filter(
					(c) => c.code === currentSupplierCurrency
				)
				i.supplierCurrency =
					findCurrency.length > 0
						? findCurrency[0].symbol
						: currentSupplierCurrency
				const currentProjectData = projectsData.filter(
					(sd) => sd.id === i.projectId
				)
				const currentProjectName =
					currentProjectData.length > 0
						? currentProjectData[0].name
						: i.projectName || 'Not there'
				i.projectName = currentProjectName
				const currentProjectNumber =
					currentProjectData.length > 0
						? currentProjectData[0].projectNumber
						: ''
				i.projectNumber = currentProjectNumber
				return i
			})

			return purchaseOrdersData
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const fetchCurrentPurchaseOrder = createAsyncThunk(
	'purchaseOrders/fetchCurrentPurchaseOrder',
	async ({ purchaseOrderId }, { getState, rejectWithValue }) => {
		try {
			const {
				data: { getPurchaseOrder },
			} = await API.graphql(
				graphqlOperation(queries.getPurchaseOrder, {
					id: purchaseOrderId,
				})
			)
			return getPurchaseOrder
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const createCurrentPurchaseOrder = createAsyncThunk(
	'purchaseOrders/createCurrentPurchaseOrder',
	async (
		{
			purchaseOrderData,
			purchaseId,
			projectId,
			recapCostlineId,
			supplierId,
			quoteId,
			quoteNumber,
			isDraft,
			isNewGeneration,
			newProvTotal,
			currentSupplier,
		},
		{ getState, dispatch, rejectWithValue }
	) => {
		const { auth } = getState()
		const tPurchaseorders = i18next.getFixedT(null, 'purchaseorders')
		try {
			console.log('purchaseOrderData: ', purchaseOrderData)
			const {
				data: { getRecapInfo: costline },
			} = await API.graphql(
				graphqlOperation(queries.getRecapInfo, { id: recapCostlineId })
			)
			if (costline?.poAvailable) {
				makeWarningToast(tPurchaseorders('toasts:alreadyExists'))
				return
			}
			const zeroPad = (num, places) => String(num).padStart(places, '0')
			const {
				data: { getCompany: companyDetails },
			} = await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: auth.userTableInfo?.currentCompanyTenantId,
				})
			)
			let newInvoiceNumber = 0
			let newInvoicingyear = companyDetails.invoicingYear
			if (companyDetails.purchaseOrderInvoiceCount === null) {
				newInvoiceNumber = zeroPad(1, 4)
			} else {
				newInvoiceNumber = zeroPad(
					Number.parseInt(companyDetails.purchaseOrderInvoiceCount) +
						1,
					4
				)
				newInvoicingyear = dayjs().toISOString()
			}
			if (companyDetails.invoicingYear === null) {
				newInvoicingyear = dayjs().toISOString()
			} else {
				const today = dayjs().year()
				const oldInvoicingYear = dayjs(
					companyDetails.invoicingYear
				).year()
				if (today > oldInvoicingYear) {
					newInvoicingyear = dayjs().toISOString()
					newInvoiceNumber = zeroPad(1, 4)
				}
			}
			const year = dayjs(newInvoicingyear).year()

			if (purchaseOrderData.poGenerated && !isDraft) {
				if (
					companyDetails.purchaseOrderInvoiceCount !==
						newInvoiceNumber ||
					companyDetails.invoicingYear !== newInvoicingyear
				) {
					const updatedCompanyDetails = {
						id: auth.userTableInfo?.currentCompanyTenantId,
						purchaseOrderInvoiceCount: newInvoiceNumber,
						invoicingYear: newInvoicingyear,
					}
					console.log(
						'updatedCompanyDetails: ',
						updatedCompanyDetails
					)
					await updateCompanyInfoWithDeltaSyncSuppress(
						updatedCompanyDetails
					)
				}
			}
			let updatePurchase = {
				projectId: projectId,
				quantity: purchaseOrderData?.quantity,
				number: purchaseOrderData?.number,
				rate: purchaseOrderData?.rate,
				taxRate: purchaseOrderData?.taxRate,
				deliveryDate: purchaseOrderData?.deliveryDate,
				deliveryAddress: purchaseOrderData?.deliveryAddress,
				poComments: purchaseOrderData?.comments,
				poGenerated: purchaseOrderData?.poGenerated,
				paymentStatus: 'PO Created',
				supplierTerms: purchaseOrderData?.supplierTerms,
			}
			if (!isNewGeneration) {
				if (purchaseOrderData.poGenerated) {
					const {
						data: { getPurchase: purchaseItem },
					} = await API.graphql(
						graphqlOperation(queries.getPurchase, {
							id: purchaseId,
						})
					)
					updatePurchase = {
						...updatePurchase,
						id: purchaseId,
					}
					console.log('isDraft: ', isDraft)
					if (updatePurchase.poGenerated && !isDraft) {
						updatePurchase.purchaseOrderNumber = `${
							companyDetails?.poNumberPrefix ?? 'PO'
						}-${year}-${newInvoiceNumber}`
					}
					if (!isNewGeneration) {
						updatePurchase = {
							...updatePurchase,
							poAvailable: true,
						}
					}
					console.log('updatePurchase: ', updatePurchase)
					await API.graphql(
						graphqlOperation(mutations.updatePurchase, {
							input: updatePurchase,
						})
					)
				}
			} else {
				const newData = {
					id: purchaseId,
					quoteId: costline?.quoteId,
					recapCostlineId: costline?.id,
					supplierId: currentSupplier?.id,
					recapCostlineItem: costline?.item,
					recapCostlineItem_fr: costline?.item_fr,
					provisionalTotal:
						newProvTotal ?? costline?.provisionalTotal,
					supplierName: currentSupplier?.name,
					supplierCreditorAccount:
						currentSupplier?.supplierCreditorAccount,
					supplierType: currentSupplier?.supplierType,

					...updatePurchase,
					paymentStatus: 'Draft',
				}
				if (newData.poGenerated && !isDraft) {
					newData.purchaseOrderNumber = `${
						companyDetails?.poNumberPrefix ?? 'PO'
					}-${year}-${newInvoiceNumber}`
				}
				if (isNewGeneration && !isDraft) {
					newData.poAvailable = true
					newData.paymentStatus = 'PO Created'
				}
				console.log('newData: ', newData)
				await dispatch(
					createPurchases({
						purchaseData: newData,
					})
				)
			}
			if (!isDraft) {
				makeSuccessToast(tPurchaseorders('toasts:createdSuccessfully'))
			} else {
				makeSuccessToast(tPurchaseorders('toasts:savedAsDraft'))
			}
			await dispatch(fetchCurrentPurchase({ purchaseId }))
			return updatePurchase
			// return newPurchaseOrder
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateCurrentPurchaseOrder = createAsyncThunk(
	'purchaseOrders/updateCurrentPurchaseOrder',
	async (
		{ purchaseOrderData, projectId, toBeGenerated, purchaseId, isDraft },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tPurchaseorders = i18next.getFixedT(null, 'purchaseorders')
		try {
			const { auth } = getState()

			let updatePOinPurchase = {}
			let poNumber = null
			if (toBeGenerated) {
				const zeroPad = (num, places) =>
					String(num).padStart(places, '0')
				const {
					data: { getCompany: companyDetails },
				} = await API.graphql(
					graphqlOperation(queries.getCompany, {
						id: auth.userTableInfo?.currentCompanyTenantId,
					})
				)
				let newInvoiceNumber = 0
				let newInvoicingyear = companyDetails.invoicingYear
				if (companyDetails.purchaseOrderInvoiceCount === null) {
					newInvoiceNumber = zeroPad(1, 4)
				} else {
					newInvoiceNumber = zeroPad(
						Number.parseInt(
							companyDetails.purchaseOrderInvoiceCount
						) + 1,
						4
					)
					newInvoicingyear = dayjs().toISOString()
				}
				if (companyDetails.invoicingYear === null) {
					newInvoicingyear = dayjs().toISOString()
				} else {
					const today = dayjs().year()
					const oldInvoicingYear = dayjs(
						companyDetails.invoicingYear
					).year()
					if (today > oldInvoicingYear) {
						newInvoicingyear = dayjs().toISOString()
						newInvoiceNumber = zeroPad(1, 4)
					}
				}
				const year = dayjs(newInvoicingyear).year()
				if (
					companyDetails.purchaseOrderInvoiceCount !==
						newInvoiceNumber ||
					companyDetails.invoicingYear !== newInvoicingyear
				) {
					const updatedCompanyDetails = {
						id: auth.userTableInfo?.currentCompanyTenantId,
						purchaseOrderInvoiceCount: newInvoiceNumber,
						invoicingYear: newInvoicingyear,
					}
					await updateCompanyInfoWithDeltaSyncSuppress(
						updatedCompanyDetails
					)
				}

				poNumber = `${
					companyDetails?.poNumberPrefix ?? 'PO'
				}-${year}-${newInvoiceNumber}`
			}

			if (purchaseId) {
				const {
					data: { getPurchase },
				} = await API.graphql(
					graphqlOperation(queries.getPurchase, {
						id: purchaseId,
					})
				)
				for (const property in purchaseOrderData) {
					// Remove poNumber received from args
					if (property === 'poNumber') continue
					if (
						!isEqual(
							purchaseOrderData[property],
							getPurchase[property]
						)
					) {
						updatePOinPurchase[property] =
							purchaseOrderData[property]
					}
				}
				if (getPurchase.projectId !== projectId) {
					updatePOinPurchase.projectId = projectId
				}
				if (poNumber) {
					updatePOinPurchase.purchaseOrderNumber = poNumber
				}
				updatePOinPurchase = {
					...updatePOinPurchase,
					id: purchaseId,
				}
				if (toBeGenerated) {
					updatePOinPurchase.poAvailable = true
					updatePOinPurchase.paymentStatus = 'PO Created'
				}
				await API.graphql(
					graphqlOperation(mutations.updatePurchase, {
						input: updatePOinPurchase,
					})
				)
			}
			if (!isDraft) {
				makeSuccessToast(tPurchaseorders('toasts.updatedSuccessfully'))
			} else {
				makeSuccessToast(tPurchaseorders('toasts.savedAsDraft'))
			}
			await dispatch(fetchCurrentPurchase({ purchaseId: purchaseId }))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const previewCurrentPurchaseOrder = createAsyncThunk(
	'purchaseOrders/previewCurrentPurchaseOrder',
	async ({ purchaseOrderData }, { getState, rejectWithValue }) => {
		try {
			if (purchaseOrderData) return purchaseOrderData
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const voidPO = createAsyncThunk(
	'purchaseOrders/voidPO',
	async ({ purchaseId }, { getState, dispatch, rejectWithValue }) => {
		console.log('purchaseId: ', purchaseId)
		const tPurchaseorders = i18next.getFixedT(null, 'purchaseorders')
		try {
			if (purchaseId) {
				const {
					data: { getPurchase },
				} = await API.graphql(
					graphqlOperation(queries.getPurchase, { id: purchaseId })
				)
				const updatePO = {
					id: getPurchase?.id,
					paymentStatus: 'void',
				}
				const {
					data: { updatePurchase },
				} = await API.graphql(
					graphqlOperation(mutations.updatePurchase, {
						input: updatePO,
					})
				)
				makeSuccessToast(
					tPurchaseorders('toasts:successfullyCancelledPO')
				)
				dispatch(setCurrentPurchase(updatePurchase))
			}
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const purchaseOrders = createSlice({
	name: 'purchaseOrders',
	initialState: {
		purchaseOrdersData: [],
		fetchPurchaseOrdersApiStatus: 'idle',
		fetchCurrentPurchaseOrderApiStatus: 'idle',
		createOrUpdateCurrentPurchaseOrderApiStatus: 'idle',
		deleteClientApiStatus: 'idle',
		voidPOApiStatus: 'idle',
		voidPOConfirmationDialogOpenStatus: false,
		pdfModalOpenStatus: false,
		openPdfModal: false,
		purchaseTotalModal: false,
		currentPurchaseOrders: null,
		previewCurrentPurchaseOrderData: null,
		error: null,
	},
	reducers: {
		setCurrentPurchaseOrders: (state, action) => {
			state.currentPurchaseOrders = action.payload
		},
		setpdfModalOpenStatus: (state, action) => {
			state.pdfModalOpenStatus = action.payload.status
		},
		setClearCurrentPurchaseOrders: (state, action) => {
			state.currentPurchaseOrders = null
		},
		setOpenPdfModal: (state, action) => {
			state.openPdfModal = action.payload
		},
		setPurchaseTotalModal: (state, action) => {
			state.purchaseTotalModal = action.payload
		},
		setVoidPOConfirmationDialogOpenStatus: (state, action) => {
			state.voidPOConfirmationDialogOpenStatus = action.payload
		},
	},
	extraReducers: {
		[fetchPurchaseOrders.pending]: (state, action) => {
			state.fetchPurchaseOrdersApiStatus = 'loading'
		},
		[fetchPurchaseOrders.fulfilled]: (state, action) => {
			state.fetchPurchaseOrdersApiStatus = 'succeeded'
			state.purchaseOrdersData = action.payload
		},
		[fetchPurchaseOrders.rejected]: (state, action) => {
			state.fetchPurchaseOrdersApiStatus = 'failed'
			state.error = action.error.message
		},
		[fetchCurrentPurchaseOrder.pending]: (state, action) => {
			state.fetchCurrentPurchaseOrderApiStatus = 'loading'
		},
		[fetchCurrentPurchaseOrder.fulfilled]: (state, action) => {
			state.fetchCurrentPurchaseOrderApiStatus = 'succeeded'
			state.currentPurchaseOrders = action.payload
		},
		[fetchCurrentPurchaseOrder.rejected]: (state, action) => {
			state.fetchCurrentPurchaseOrderApiStatus = 'failed'
			state.error = action.error.message
		},
		[createCurrentPurchaseOrder.pending]: (state, action) => {
			state.createOrUpdateCurrentPurchaseOrderApiStatus = 'loading'
			state.currentPurchaseOrders = action.meta.arg.purchaseOrderData
		},
		[createCurrentPurchaseOrder.fulfilled]: (state, action) => {
			state.createOrUpdateCurrentPurchaseOrderApiStatus = 'succeeded'
			state.currentPurchaseOrders = action.payload
		},
		[createCurrentPurchaseOrder.rejected]: (state, action) => {
			state.createOrUpdateCurrentPurchaseOrderApiStatus = 'failed'
			state.error = action.error.message
		},
		[updateCurrentPurchaseOrder.pending]: (state, action) => {
			state.createOrUpdateCurrentPurchaseOrderApiStatus = 'loading'
			// state.currentPurchaseOrders = action.meta.arg.purchaseOrderData
		},
		[updateCurrentPurchaseOrder.fulfilled]: (state, action) => {
			state.createOrUpdateCurrentPurchaseOrderApiStatus = 'succeeded'
			// state.currentPurchaseOrders = action.payload
		},
		[updateCurrentPurchaseOrder.rejected]: (state, action) => {
			state.createOrUpdateCurrentPurchaseOrderApiStatus = 'failed'
			state.error = action.error.message
		},
		[previewCurrentPurchaseOrder.pending]: (state, action) => {
			state.previewCurrentPurchaseOrderApiStatus = 'loading'
			state.previewCurrentPurchaseOrderData =
				action.meta.arg.purchaseOrderData
		},
		[previewCurrentPurchaseOrder.fulfilled]: (state, action) => {
			state.previewCurrentPurchaseOrderApiStatus = 'succeeded'
			state.previewCurrentPurchaseOrderData = action.payload
		},
		[previewCurrentPurchaseOrder.rejected]: (state, action) => {
			state.previewCurrentPurchaseOrderApiStatus = 'failed'
			state.error = action.error.message
		},
		[voidPO.pending]: (state, action) => {
			state.voidPOApiStatus = 'loading'
		},
		[voidPO.fulfilled]: (state, action) => {
			state.voidPOApiStatus = 'succeeded'
			state.voidPOConfirmationDialogOpenStatus = false
		},
		[voidPO.rejected]: (state, action) => {
			state.voidPOApiStatus = 'failed'
			state.error = action.error.message
		},
	},
})

export const {
	setCurrentPurchaseOrders,
	setClearCurrentPurchaseOrders,
	setpdfModalOpenStatus,
	setOpenPdfModal,
	setPurchaseTotalModal,
	setVoidPOConfirmationDialogOpenStatus,
} = purchaseOrders.actions

export default purchaseOrders.reducer
